import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WishlistIdsDto, WishlistKind } from "@gaole/shared";
import { api } from "../lib/api";
import { useAuth } from "../stores/auth";

export function useWishlistIds() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["wishlist", "ids"],
    queryFn: () => api<WishlistIdsDto>("/wishlist/ids"),
    enabled: Boolean(user),
    staleTime: 30_000,
  });
}

export function useWishlistToggle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: WishlistKind; productId?: string; listingId?: string }) =>
      api<{ saved: boolean }>("/wishlist/toggle", { method: "POST", body }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ["wishlist", "ids"] });
      const prev = qc.getQueryData<WishlistIdsDto>(["wishlist", "ids"]);
      if (prev) {
        const next = { ...prev };
        if (body.kind === "SHOP" && body.productId) {
          const on = prev.productIds.includes(body.productId);
          next.productIds = on
            ? prev.productIds.filter((id) => id !== body.productId)
            : [...prev.productIds, body.productId];
        }
        if (body.kind === "MARKETPLACE" && body.listingId) {
          const on = prev.listingIds.includes(body.listingId);
          next.listingIds = on
            ? prev.listingIds.filter((id) => id !== body.listingId)
            : [...prev.listingIds, body.listingId];
        }
        qc.setQueryData(["wishlist", "ids"], next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["wishlist", "ids"], ctx.prev);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["wishlist"] });
    },
  });
}
